import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';
import { openPreferences } from '../../../lib/consent';
import './FooterSection.css';

function FooterSection() {
  const isIOSSafari = typeof document !== 'undefined' && document.documentElement.classList.contains('ios-safari');
  const inViewViewport = isIOSSafari ? { once: true, amount: 0.25 } : { once: true };

  const currentYear = new Date().getFullYear();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  const socialLinks = [
    { icon: <Facebook className="w-5 h-5" />, label: 'Facebook', href: 'https://www.facebook.com/people/Versa-Launch-LLC/61580890046419/' },
    { icon: <Instagram className="w-5 h-5" />, label: 'Instagram', href: 'https://www.instagram.com/versalaunch/' },
    { icon: <Linkedin className="w-5 h-5" />, label: 'LinkedIn', href: 'https://www.linkedin.com/company/versa-launch/' },
    { icon: <Twitter className="w-5 h-5" />, label: 'X (Twitter)', href: 'https://x.com/VersaLaunch' }
  ];

  return (
    <footer className="footer-section">
      <div className="footer-content">
        <div className="container">
          {/* Footer Grid */}
          <motion.div
            className="footer-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={inViewViewport}
          >
            {/* Company Info */}
            <motion.div className="footer-column" variants={itemVariants}>
              <h3 className="footer-heading"><span style={{ color: '#d4af37' }}>VERSA</span> <span style={{ color: '#ffffff' }}>LAUNCH</span></h3>
              <p className="footer-description">
                Building digital empires for businesses that want to dominate their market. We create stunning websites that convert visitors into customers.
              </p>
              <div className="social-links">
                {socialLinks.map((social, idx) => (
                  <motion.a
                    key={idx}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="social-link"
                    whileHover={{ scale: 1.2, rotate: 5 }}
                    whileTap={{ scale: 0.95 }}
                    title={social.label}
                  >
                    {social.icon}
                  </motion.a>
                ))}
              </div>
            </motion.div>

            {/* Quick Links */}
            <motion.div className="footer-column" variants={itemVariants}>
              <h4 className="footer-subheading">Quick Links</h4>
              <ul className="footer-links">
                <li><Link to="/">Home</Link></li>
                <li><Link to="/plans">Pricing</Link></li>
                <li><Link to="/services">Services</Link></li>
                <li><Link to="/resources/case-studies">Portfolio</Link></li>
                <li><Link to="/careers">Careers</Link></li>
                <li><Link to="/blog">Blog</Link></li>
              </ul>
            </motion.div>

            {/* Services */}
            <motion.div className="footer-column" variants={itemVariants}>
              <h4 className="footer-subheading">Services</h4>
              <ul className="footer-links">
                <li><Link to="/services/custom-design">Web Design</Link></li>
                <li><Link to="/resources/seo-guide">SEO Optimization</Link></li>
                <li><Link to="/services/ai-phone-assistant">Lead Generation</Link></li>
                <li><Link to="/services/web-apps">E-Commerce</Link></li>
              </ul>
            </motion.div>

            {/* Contact Info */}
            <motion.div className="footer-column" variants={itemVariants}>
              <h4 className="footer-subheading">Contact</h4>
              <div className="contact-info">
                <a href="tel:19047504885" className="contact-link">
                  <Phone className="w-4 h-4" />
                  <span>(904) 750-4885</span>
                </a>
                <a href="mailto:info@versalaunch.com" className="contact-link">
                  <Mail className="w-4 h-4" />
                  <span>info@versalaunch.com</span>
                </a>
                <div className="contact-link">
                  <MapPin className="w-4 h-4" />
                  <span>Jacksonville, FL</span>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Footer Bottom */}
          <motion.div
            className="footer-bottom"
            variants={itemVariants}
            initial="hidden"
            whileInView="visible"
            viewport={inViewViewport}
            transition={{ delay: 0.4 }}
          >
            <p className="footer-copyright">
              &copy; {currentYear} Versa Launch. All rights reserved.
            </p>
            <div className="footer-links-bottom">
              <Link to="/privacy-policy">Privacy Policy</Link>
              <span>•</span>
              <Link to="/terms-of-service">Terms of Service</Link>
              <span>•</span>
              <button
                type="button"
                onClick={openPreferences}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit',
                  color: 'inherit',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                Cookie preferences
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </footer>
  );
}

export default FooterSection;
